import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Chip,
} from '@mui/material';
import { 
  Search, 
  Assessment,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Receipt,
  AttachMoney,
  TrendingUp,
  Business,
  Description,
  MonetizationOn,
  Refresh
} from '@mui/icons-material';

const PayoutSummary = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [payoutSearch, setPayoutSearch] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Sample payout summary data
  const [payoutSummaryData] = useState([
    {
      id: 1,
      payoutNo: 'PO001',
      closingDate: '2024-01-15',
      businessAmount: 250000,
      directAmount: 125000,
      differenceAmount: 5000,
      total: 380000,
    },
    {
      id: 2,
      payoutNo: 'PO002',
      closingDate: '2024-01-20',
      businessAmount: 180000,
      directAmount: 85000,
      differenceAmount: 3000,
      total: 268000,
    },
    {
      id: 3,
      payoutNo: 'PO003',
      closingDate: '2024-01-25',
      businessAmount: 320000,
      directAmount: 200000,
      differenceAmount: 8000,
      total: 528000,
    },
    {
      id: 4,
      payoutNo: 'PO004',
      closingDate: '2024-02-01',
      businessAmount: 290000,
      directAmount: 150000,
      differenceAmount: 6000,
      total: 446000,
    },
    {
      id: 5,
      payoutNo: 'PO005',
      closingDate: '2024-02-05',
      businessAmount: 165000,
      directAmount: 95000,
      differenceAmount: 4000,
      total: 264000,
    },
    {
      id: 6,
      payoutNo: 'PO006',
      closingDate: '2024-02-10',
      businessAmount: 310000,
      directAmount: 175000,
      differenceAmount: 7000,
      total: 492000,
    },
    {
      id: 7,
      payoutNo: 'PO007',
      closingDate: '2024-02-15',
      businessAmount: 225000,
      directAmount: 110000,
      differenceAmount: 5500,
      total: 340500,
    },
    {
      id: 8,
      payoutNo: 'PO008',
      closingDate: '2024-02-20',
      businessAmount: 275000,
      directAmount: 135000,
      differenceAmount: 6200,
      total: 416200,
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    console.log('Searching for payout:', payoutSearch);
    setSuccessMessage(`Search completed! Found ${getFilteredPayoutData().length} payout record(s).`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleReset = () => {
    setPayoutSearch('');
    setSuccessMessage('');
  };

  const getFilteredPayoutData = () => {
    let filteredData = payoutSummaryData;

    // Filter by payout number
    if (payoutSearch.trim()) {
      filteredData = filteredData.filter(item =>
        item.payoutNo.toLowerCase().includes(payoutSearch.toLowerCase())
      );
    }

    return filteredData;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getSearchSummary = () => {
    const total = getFilteredPayoutData().length;
    const totalAmount = getFilteredPayoutData().reduce((sum, payout) => sum + payout.total, 0);
    return `Showing ${total} payout record(s) with total amount of ${formatCurrency(totalAmount)}`;
  };

  // Summary calculations
  const totalPayouts = payoutSummaryData.length;
  const totalBusinessAmount = payoutSummaryData.reduce((sum, payout) => sum + payout.businessAmount, 0);
  const totalDirectAmount = payoutSummaryData.reduce((sum, payout) => sum + payout.directAmount, 0);
  const totalDifferenceAmount = payoutSummaryData.reduce((sum, payout) => sum + payout.differenceAmount, 0);

  // Calculate totals for all filtered data
  const calculateTotals = () => {
    const filteredData = getFilteredPayoutData();
    return filteredData.reduce(
      (totals, item) => ({
        businessAmount: totals.businessAmount + item.businessAmount,
        directAmount: totals.directAmount + item.directAmount,
        differenceAmount: totals.differenceAmount + item.differenceAmount,
        total: totals.total + item.total,
      }),
      { businessAmount: 0, directAmount: 0, differenceAmount: 0, total: 0 }
    );
  };

  const totals = calculateTotals();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <Assessment sx={{ color: '#1976d2', fontSize: 32 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
            Payout Summary Management
          </Typography>
        </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Payouts
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalPayouts}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Business sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Business Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalBusinessAmount / 100000).toFixed(1)}L
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <AttachMoney sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Direct Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalDirectAmount / 100000).toFixed(1)}L
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <TrendingUp sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Difference Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalDifferenceAmount / 1000).toFixed(0)}K
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#f3e5f5', 
            border: '1px solid #e1bee7',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <MonetizationOn sx={{ color: '#7b1fa2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totals.total / 100000).toFixed(1)}L
      </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Description sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search & Filter Payout Summary
        </Typography>
          </Box>
          
          {/* <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              Search and filter payout summary records by payout number
            </Box>
          </Alert> */}
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
                label="Payout Number"
              value={payoutSearch}
              onChange={(e) => setPayoutSearch(e.target.value)}
              placeholder="Enter payout number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
            />
          </Grid>
          
            <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
                startIcon={<Search />}
              onClick={handleSearch}
              fullWidth
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
            >
              Search
            </Button>
          </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Refresh />}
                onClick={handleReset}
                fullWidth
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                Reset
            </Button>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Payout Summary Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Assessment sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
          Payout Summary Data
        </Typography>
          </Box>
        
        
        {getFilteredPayoutData().length > 0 ? (
            <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Table>
              <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      S.No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Payout Info
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Closing Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Business Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Direct Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Difference Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Total Amount
                    </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredPayoutData()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((payout, index) => (
                      <TableRow key={payout.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </TableCell>
                      <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Receipt sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {payout.payoutNo}
                        </Typography>
                          </Box>
                      </TableCell>
                      <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarToday sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {payout.closingDate}
                        </Typography>
                          </Box>
                      </TableCell>
                      <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Business sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          {formatCurrency(payout.businessAmount)}
                        </Typography>
                          </Box>
                      </TableCell>
                      <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#f57c00' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                          {formatCurrency(payout.directAmount)}
                        </Typography>
                          </Box>
                      </TableCell>
                      <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TrendingUp sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                          {formatCurrency(payout.differenceAmount)}
                        </Typography>
                          </Box>
                      </TableCell>
                      <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <MonetizationOn sx={{ fontSize: 16, color: '#7b1fa2' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                          {formatCurrency(payout.total)}
                        </Typography>
                          </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                
                {/* Summary Row */}
                <TableRow sx={{ backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Assessment sx={{ fontSize: 20, color: '#1976d2' }} />
                    <Typography variant="h6" color="primary.main">
                      GRAND TOTAL
                    </Typography>
                      </Box>
                  </TableCell>
                  <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Business sx={{ fontSize: 16, color: '#2e7d32' }} />
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      {formatCurrency(totals.businessAmount)}
                    </Typography>
                      </Box>
                  </TableCell>
                  <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AttachMoney sx={{ fontSize: 16, color: '#f57c00' }} />
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                      {formatCurrency(totals.directAmount)}
                    </Typography>
                      </Box>
                  </TableCell>
                  <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TrendingUp sx={{ fontSize: 16, color: '#2e7d32' }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(totals.differenceAmount)}
                    </Typography>
                      </Box>
                  </TableCell>
                  <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <MonetizationOn sx={{ fontSize: 16, color: '#7b1fa2' }} />
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#7b1fa2', fontSize: '1.1rem' }}>
                      {formatCurrency(totals.total)}
                    </Typography>
                      </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              
              component="div"
              count={getFilteredPayoutData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        ) : (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
              No Payout Records Found
            </Typography>
                  <Typography variant="body2">
                    No payout records match your search criteria. Try adjusting your search terms.
            </Typography>
          </Box>
              </Box>
            </Alert>
        )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PayoutSummary;